import { useMutation, useQuery } from '@apollo/client';
import {
  AccessGrant,
  Door,
  Mutation,
  MutationDeleteLocationArgs,
  MutationUnassignFromAccessGrantsArgs,
  MutationUpdateLocationArgs,
  Query,
  VisitorGroup
} from 'graphql/generated/graphqlTypes';
import {
  ASSIGN_DEVICE_GROUP_TO_ACCESS_GRANTS,
  DELETE_DEVICE_GROUP,
  UNASSIGN_DEVICE_GROUP_FROM_ACCESS_GRANTS,
  UPDATE_DEVICE_GROUP
} from 'graphql/v2/mutations';
import {
  GET_ALL_DOORS_FOR_DEVICE_GROUPS,
  GET_ALL_VISITORS_GROUPS,
  GET_DEVICE_GROUP_DETAIL_DOORS,
  GET_DEVICE_GROUPS_WITH_DOORS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect } from 'react';
import { getAllAccessGrantsOfVisitorGroup } from 'utils/VisitorGroup/VisitorGroup';

export type UserGroupRow = VisitorGroup & {
  accessGrant: AccessGrant | undefined;
  isEnabled: boolean;
};

interface UseDeviceGroupDetailsProps extends BaseHookProps {
  groupId: string;
}

const useDeviceGroupDetails = ({ groupId, handleFetchError }: UseDeviceGroupDetailsProps) => {
  const { translation } = useTranslation();
  const {
    loading: locationsLoading,
    error: locationsError,
    data: locationsData,
    refetch: refetchLocations
  } = useQuery<Pick<Query, 'getLocationsForTenant'>>(GET_DEVICE_GROUP_DETAIL_DOORS);

  const {
    loading: doorsLoading,
    error: doorsError,
    data: doorsData
  } = useQuery<Pick<Query, 'getDoorsForTenant'>>(GET_ALL_DOORS_FOR_DEVICE_GROUPS);

  const {
    loading: visitorGroupsLoading,
    error: visitorGroupsError,
    data: visitorGroupsData
  } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_ALL_VISITORS_GROUPS);

  useEffect(() => {
    if (locationsError) {
      handleFetchError(translation.device_group_error_fetching_details);
      console.error(locationsError);
    }
  }, [locationsError]);

  useEffect(() => {
    if (visitorGroupsError) {
      handleFetchError(translation.device_group_error_fetching_user_groups);
      console.error(visitorGroupsError);
    }
  }, [visitorGroupsError]);

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.device_group_error_fetching_details);
      console.error(doorsError);
    }
  }, [doorsError]);

  const allLocations = locationsData?.getLocationsForTenant ?? [];
  const deviceGroup = allLocations.find((loc) => loc.id === groupId);
  const doorsInGroup = deviceGroup?.doors ?? [];
  const allDoors: Door[] = doorsData?.getDoorsForTenant ?? [];

  const allVisitorGroups = visitorGroupsData?.getVisitorGroupsForTenant ?? [];

  const userGroupsForGroup: UserGroupRow[] = allVisitorGroups.map((group) => {
    const isEnabled = getAllAccessGrantsOfVisitorGroup(group).some((grant) =>
      grant.locations.some((loc) => loc.id === groupId)
    );
    const accessGrant = group.accessGrants[0];
    return { ...group, accessGrant, isEnabled };
  });

  const [deleteLocationMutation] = useMutation<Pick<Mutation, 'deleteLocation'>, MutationDeleteLocationArgs>(
    DELETE_DEVICE_GROUP,
    {
      refetchQueries: [GET_DEVICE_GROUPS_WITH_DOORS, GET_DEVICE_GROUP_DETAIL_DOORS],
      awaitRefetchQueries: true
    }
  );

  // Both mutations return the affected AccessGrant with `id` + `locations`, so Apollo's
  // normalized cache updates that entity in place wherever it's referenced (e.g. inside
  // GET_ALL_VISITORS_GROUPS) — no refetch needed to keep this page's data fresh.
  const [unassignFromAccessGrantsMutation] = useMutation<
    Pick<Mutation, 'unassignFromAccessGrants'>,
    MutationUnassignFromAccessGrantsArgs
  >(UNASSIGN_DEVICE_GROUP_FROM_ACCESS_GRANTS);

  const [assignToAccessGrantsMutation] = useMutation(ASSIGN_DEVICE_GROUP_TO_ACCESS_GRANTS);

  const [updateDeviceGroupMutation] = useMutation<Pick<Mutation, 'updateLocation'>, MutationUpdateLocationArgs>(
    UPDATE_DEVICE_GROUP,
    {
      refetchQueries: (result) => [
        { query: GET_DEVICE_GROUP_DETAIL_DOORS, variables: { filter: { id: result.data?.updateLocation?.id } } }
      ],
      awaitRefetchQueries: true
    }
  );

  const deleteDeviceGroup = async (): Promise<{ success: boolean; errorMessage?: string }> => {
    try {
      await deleteLocationMutation({ variables: { id: groupId } });
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : translation.an_error_occurred;
      return { success: false, errorMessage };
    }
  };

  const toggleUserGroupAccess = async (userGroup: UserGroupRow, enabled: boolean): Promise<void> => {
    const allAccessGrantIds = userGroup.accessGrants.map((grant) => grant.id);
    if (allAccessGrantIds.length === 0) return;

    if (enabled) {
      await assignToAccessGrantsMutation({
        variables: { accessGrantIds: allAccessGrantIds, doorIds: [], locationIds: [groupId] }
      });
    } else {
      await unassignFromAccessGrantsMutation({
        variables: { accessGrantIds: allAccessGrantIds, doorIds: [], locationIds: [groupId] }
      });
    }
  };

  const updateDeviceGroupWithDoors = async (
    doorIds: string[]
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    if (!deviceGroup) return { success: false, errorMessage: translation.device_group_error_updating_devices };
    try {
      await updateDeviceGroupMutation({
        variables: { id: groupId, location: { name: deviceGroup.name, doorIds } }
      });
      return { success: true };
    } catch (err) {
      return {
        success: false,
        errorMessage: err instanceof Error ? err.message : translation.device_group_error_updating_devices
      };
    }
  };

  const renameDeviceGroup = async (name: string): Promise<{ success: boolean; errorMessage?: string }> => {
    if (!deviceGroup) return { success: false, errorMessage: translation.device_group_error_updating_name };
    try {
      await updateDeviceGroupMutation({
        variables: {
          id: groupId,
          location: { name, doorIds: doorsInGroup.map((door) => door.id) }
        }
      });
      return { success: true };
    } catch (err) {
      return {
        success: false,
        errorMessage: err instanceof Error ? err.message : translation.device_group_error_updating_name
      };
    }
  };

  return {
    deviceGroup,
    doorsInGroup,
    allDoors,
    userGroupsForGroup,
    loading: locationsLoading || visitorGroupsLoading || doorsLoading,
    hasDeviceGroupFetchError: Boolean(locationsError),
    retryFetchingDeviceGroup: () => refetchLocations(),
    deleteDeviceGroup,
    toggleUserGroupAccess,
    updateDeviceGroupWithDoors,
    renameDeviceGroup
  };
};

export default useDeviceGroupDetails;
